import type { DetectedLanguage, SpokenLanguage } from '../types/LocalAiTypes';
import { extractDateExpression } from './DateExpressionNormalizer';
import { normalizeEnglish } from './EnglishNormalizer';
import { normalizeSpokenNumbers } from './NumberNormalizer';
import { normalizePersianCharacters } from './PersianNormalizer';

export interface NormalizedTranscript {
  rawText: string;
  normalizedText: string;
  language: DetectedLanguage;
}

export function detectLanguage(value: string): DetectedLanguage {
  if (/[پچژگکی]/u.test(value)) return 'fa';
  if (/[ةىأإآؤئكي]/u.test(value)) return 'ar';
  // Ambiguous Arabic-script text keeps the historical Persian default. An explicit microphone
  // language is passed as a hint, so Arabic recognition never depends on this fallback.
  if (/[\u0600-\u06ff]/u.test(value)) return 'fa';
  if (/[a-z]/iu.test(value)) return 'en';
  return 'unknown';
}

export function normalizeTranscript(rawText: string, languageHint?: SpokenLanguage): NormalizedTranscript {
  const language = languageHint ?? detectLanguage(rawText);
  const characters = normalizeEnglish(language === 'ar'
    ? normalizeArabicCharacters(rawText)
    : normalizePersianCharacters(rawText));
  const punctuation = characters
    .replace(/[،؛,:;!?؟"“”'()[\]{}]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
  return {
    rawText,
    normalizedText: normalizeSpokenNumbers(punctuation).replace(/\s+/gu, ' ').trim(),
    language,
  };
}

const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
const arabicDigits = '٠١٢٣٤٥٦٧٨٩';

/** Arabic keeps its own letter forms while sharing digit and whitespace normalization. */
function normalizeArabicCharacters(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[ً-ٰٟ]/gu, '')
    .replace(/[​‌‍⁠]/gu, ' ')
    .replace(/[۰-۹]/gu, (digit) => String(persianDigits.indexOf(digit)))
    .replace(/[٠-٩]/gu, (digit) => String(arabicDigits.indexOf(digit)));
}

export function extractDeterministicParameters(normalized: string): Record<string, unknown> {
  const parameters: Record<string, unknown> = {};
  const period = extractDateExpression(normalized);
  if (period) parameters.period = period;

  const percentage = /(?:%|درصد|percent|نسبة|بالمئة)\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s*(?:%|درصد|percent|نسبة|بالمئة)/iu.exec(normalized);
  const percentageValue = percentage?.[1] ?? percentage?.[2];
  if (percentageValue) parameters.percentage = Number(percentageValue);

  const quantity = /(?:quantity|تعداد|مقدار|كمية|عدد)?\s*(\d+(?:\.\d+)?)\s*(?:عدد|تا|items?|units?|عناصر|وحدات)(?=$|\s)/iu.exec(normalized);
  if (quantity?.[1]) parameters.quantity = Number(quantity[1]);

  const number = /(?:شماره|number|no|رقم)\s*#?\s*([a-z0-9][a-z0-9/-]{0,39})\b/iu.exec(normalized);
  if (number?.[1]) parameters.documentNumber = number[1];

  const currency = /(?:^|\s)(irr|ریال|ريال|تومان|usd|دلار|دولار|dollars?|eur|یورو|يورو|euros?)(?=$|\s)/iu.exec(normalized)?.[1];
  if (currency) parameters.currency = currency;
  const amount = /(\d+(?:\.\d+)?)\s*(?:irr|ریال|ريال|تومان|usd|دلار|دولار|dollars?|eur|یورو|يورو|euros?)/iu.exec(normalized)?.[1];
  if (amount) parameters.amount = Number(amount);

  if (/(?:^|\s)(unpaid|پرداخت نشده|تسویه نشده|غير مدفوع)(?=$|\s)/iu.test(normalized)) parameters.status = 'unpaid';
  if (/(?:^|\s)(overdue|سررسید گذشته|معوق|متأخر|متأخرة)(?=$|\s)/iu.test(normalized)) parameters.status = 'overdue';
  if (/(?:^|\s)(low stock|کم موجودی|مخزون منخفض)(?=$|\s)/iu.test(normalized)) parameters.status = 'lowStock';
  if (/(?:^|\s)(out of stock|ناموجود|غير متوفر)(?=$|\s)/iu.test(normalized)) parameters.status = 'outOfStock';

  const sku = /(?:^|\s)(?:sku|product|کالا|محصول|منتج|صنف)\s+([a-z][a-z0-9_-]{1,31})\b/iu.exec(normalized)?.[1];
  if (sku) parameters.sku = sku.toLocaleUpperCase('en-US');
  const barcode = /(?:^|\s)(?:barcode|بارکد|باركود)\s+([0-9]{4,32})\b/iu.exec(normalized)?.[1];
  if (barcode) parameters.barcode = barcode;
  const page = /(?:page|صفحه|صفحة)\s+(\d{1,4})\b/iu.exec(normalized)?.[1];
  if (page) parameters.pageNumber = Number(page);
  return parameters;
}
